import { fromFile } from "./../bundled-dependencies/id3js.js";
import {
  createOpenMediaManagerFn,
  MediaManagerMediaType
} from "./../_shared/create-open-media-manager-fn.js";
import { getUploadError } from "./../_shared/get-upload-error.js";
import { logInvariant } from "./../_shared/log-invariant.js";
import { MediaType } from "./../_shared/request-service-provider/consts.js";
import { createUploadFn } from "./../_shared/request-service-provider/media-manager-uploader.js";
import { upload } from "./../_shared/upload/upload.js";
import { withFileSizeLimitation } from "./../_shared/with-file-size-limitation.js";
import { audioFileTypes } from ".//consts.js";
import { RICOS_DEFAULTS } from ".//defaults.js";
import { AUDIO_TYPE } from "./types.js";
const getAudioPickMedia = ({
  services,
  type,
  handleFileUpload
}) => {
  const devPickMedia = services.devService().pickMedia;
  const editorPickMedia = type === "audio" ? services.editorProps.pickMedia?.pickAudio : services.editorProps.pickMedia?.pickImage;
  const handleFileSelection = services.devService().handleFileSelection ?? createHandleFileSelectionFn(services);
  return devPickMedia ? devPickMedia : editorPickMedia ? editorPickMedia : handleFileSelection ? async () => {
    const data = await new Promise(
      (resolve) => handleFileSelection(({ data: data2 }) => resolve(data2), { isCoverImage: type === "image" })
    );
    return { type: "ready-media", media: [data] };
  } : async ({ mimeType }) => {
    const files = await services.uploadService?.selectFiles({ accept: mimeType, multiple: false }) ?? [];
    return { type: "file", file: files, customUploadFn: handleFileUpload };
  };
};
const isPickMediaAudioType = (data) => "type" in data && data.type === "audio";
const isPickMediaImageType = (data) => "type" in data && data.type === "image";
const getAudioAttributes = ({ type, uploadData, componentData }) => {
  if (type === "audio") {
    if (isPickMediaAudioType(uploadData)) {
      return {
        ...componentData,
        audio: { src: uploadData.src },
        name: uploadData.title ?? componentData.name ?? "",
        authorName: uploadData.authorName ?? componentData?.authorName ?? "",
        html: null,
        tempData: void 0,
        loading: void 0
      };
    }
    return {
      ...componentData,
      audio: uploadData?.audio ?? componentData?.audio ?? null,
      name: uploadData?.name ?? componentData?.name ?? "",
      authorName: uploadData?.authorName ?? componentData?.authorName ?? "",
      html: null,
      tempData: void 0,
      loading: void 0
    };
  }
  if (isPickMediaImageType(uploadData)) {
    return {
      ...componentData,
      coverImage: {
        src: uploadData.src,
        width: uploadData.width,
        height: uploadData.height
      },
      tempData: void 0,
      loading: void 0
    };
  }
  return {
    ...componentData,
    coverImage: {
      src: { id: uploadData.file_name },
      width: uploadData.width,
      height: uploadData.height
    },
    tempData: void 0,
    loading: void 0
  };
};
const handleAudioUpload = async ({
  services,
  publicCommands,
  nodeId,
  type
}) => {
  const mimeType = type === "audio" ? audioFileTypes : "image/*";
  const handleFileUpload = createHandleFileUploadFn(services);
  const pickMedia = getAudioPickMedia({ services, type, handleFileUpload });
  const result = await pickMedia({ mimeType, multiple: false });
  switch (result.type) {
    case "ready-media": {
      const id = nodeId || publicCommands.insertBlockWithBlankLines(AUDIO_TYPE, RICOS_DEFAULTS);
      setTimeout(() => {
        const componentData = publicCommands?.getBlockComponentData(id);
        const updatedData = getAudioAttributes(
          type === "audio" ? {
            type: "audio",
            componentData,
            uploadData: result.media[0]
          } : {
            type: "image",
            componentData,
            uploadData: result.media[0]
          }
        );
        publicCommands?.setBlock(id, AUDIO_TYPE, {
          ...updatedData,
          tempData: void 0,
          loading: void 0,
          error: void 0
        });
      });
      return;
    }
    case "file": {
      const file = result.file[0];
      if (file) {
        const tags = await getAudioTags(file);
        const targetNodeId = nodeId || publicCommands.insertBlockWithBlankLines(AUDIO_TYPE, {
          ...RICOS_DEFAULTS,
          ...tags,
          loading: true
        });
        const trackBi = services.uploadService?.trackUploadBi(AUDIO_TYPE, file.size, file.name);
        try {
          const url = URL.createObjectURL(file);
          await Promise.resolve();
          const componentData = publicCommands?.getBlockComponentData(targetNodeId);
          publicCommands?.setBlock(targetNodeId, AUDIO_TYPE, {
            ...componentData,
            name: componentData.name || file.name.split(".")[0],
            tempData: url,
            loading: true,
            error: null
          });
          trackBi?.start();
          const uploadFn = result.customUploadFn || handleFileUpload;
          if (!uploadFn) {
            logInvariant(Error("No upload function provided"));
            throw new Error("No upload function provided");
          }
          const uploadedFile = await upload(uploadFn, file);
          const updatedData = getAudioAttributes({
            type,
            componentData: publicCommands?.getBlockComponentData(targetNodeId),
            uploadData: uploadedFile.data
          });
          publicCommands?.setBlock(targetNodeId, AUDIO_TYPE, updatedData);
          URL.revokeObjectURL(url);
          trackBi?.success();
        } catch (error) {
          const componentData = publicCommands?.getBlockComponentData(targetNodeId);
          publicCommands?.setBlock(targetNodeId, AUDIO_TYPE, {
            ...componentData,
            error,
            loading: null
          });
          trackBi?.error(error);
          services.uploadService?.notifyError(error);
        }
      } else {
        console.error("No file selected");
      }
      return;
    }
    case "cancel": {
      return;
    }
    default: {
      console.error("Unhandeled media picker result:", result);
      return;
    }
  }
};
const getAudioTags = async (file) => {
  try {
    const tags = await fromFile(file);
    return { name: tags?.title, authorName: tags?.artist };
  } catch (error) {
    console.error(error);
    return {};
  }
};
const createHandleFileUploadFn = (services) => {
  const { metaSiteId, uid, siteOwnerId } = services.wixInstance();
  if (!metaSiteId) {
    return void 0;
  }
  const audioUpload = (file, updateEntity) => {
    const upload2 = createUploadFn(services);
    const isCoverImage = file.type.includes("image");
    const mediaType = isCoverImage ? MediaType.IMAGE : MediaType.AUDIO;
    return upload2(file, mediaType).then((res) => {
      if (isCoverImage) {
        const { file_name, original_file_name, height, width } = res;
        return updateEntity({
          data: {
            id: file_name,
            height,
            width,
            original_file_name,
            file_name
          }
        });
      } else {
        const {
          file_output: { audio }
        } = res;
        updateEntity({
          data: {
            audio: { src: { id: audio[0].url } }
          }
        });
      }
    }).catch((error) => {
      const isOwner = siteOwnerId === uid;
      const mediaUploadError = getUploadError(error, metaSiteId, isOwner);
      updateEntity({ error: mediaUploadError });
    });
  };
  return withFileSizeLimitation(audioUpload, services.editorProps.fileUploadSizeLimit);
};
const createHandleFileSelectionFn = (services) => {
  const openMediaManager = createOpenMediaManagerFn();
  if (!openMediaManager) {
    return void 0;
  }
  const audioUpload = (updateEntity, options) => {
    if (options.isCoverImage) {
      openMediaManager({
        mediaType: MediaManagerMediaType.IMAGE,
        appDefId: services.wixInstance().appDefId
      }).then((items) => {
        const file = items[0];
        const { uri, width, height, title } = file;
        updateEntity({
          data: { id: uri, file_name: uri, original_file_name: title, width, height }
        });
      });
      return;
    }
    openMediaManager({
      mediaType: MediaManagerMediaType.AUDIO,
      appDefId: services.wixInstance().appDefId
    }).then((items) => {
      const file = items[0];
      const { audio } = file.fileOutput;
      const name = file.title.split(".")[0];
      updateEntity({
        data: { audio: { src: { id: audio[0].url } }, name }
      });
    });
  };
  return audioUpload;
};
export {
  createHandleFileUploadFn,
  handleAudioUpload
};
//# sourceMappingURL=handle-audio-upload.js.map
